import { Component, ErrorInfo, ReactNode } from "react";
import { logger } from "kmstack-shared/core";
import { Button } from "@/components/ui/Button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}
interface GlobalErrorFallbackProps {
    error: unknown;
    resetErrorBoundary: () => void;
    showStackTrace?: boolean; // Optional toggle
}

export function GlobalErrorFallback({
    error,
    resetErrorBoundary,
    showStackTrace = import.meta.env.MODE === "development", // Show by default in dev
}: GlobalErrorFallbackProps) {
    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900">
                Something went wrong
            </h1>
            <p className="mb-8 max-w-md text-gray-600">
                We apologize for the inconvenience. An unexpected error has occurred.
                Our team has been notified.
            </p>
            <div className="flex gap-4">
                <Button onClick={resetErrorBoundary} className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Reload Application
                </Button>
            </div>
            {showStackTrace && Boolean(error) && (
                <div className="mt-8 overflow-hidden rounded-lg bg-gray-900 p-4 text-left shadow-lg max-w-2xl w-full">
                    <p className="mb-2 font-mono text-sm font-bold text-red-400">
                        {String(error)}
                    </p>
                    <div className="overflow-x-auto">
                        <pre className="font-mono text-xs text-gray-300">
                            {error instanceof Error ? error.stack : undefined}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
/**@deprecated */
export class GlobalErrorFallback2 extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        logger.error("Uncaught error:", error);
        logger.error("Error Info:", errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 text-center">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                        <AlertTriangle className="h-8 w-8 text-red-600" />
                    </div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">
                        Something went wrong
                    </h1>
                    <p className="mb-8 max-w-md text-gray-600">
                        We apologize for the inconvenience. An unexpected error has occurred.
                        Our team has been notified.
                    </p>
                    <div className="flex gap-4">
                        <Button onClick={this.handleReload} className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4" />
                            Reload Application
                        </Button>
                    </div>
                    {process.env.NODE_ENV === "development" && this.state.error && (
                        <div className="mt-8 overflow-hidden rounded-lg bg-gray-900 p-4 text-left shadow-lg max-w-2xl w-full">
                            <p className="mb-2 font-mono text-sm font-bold text-red-400">
                                {this.state.error.toString()}
                            </p>
                            <div className="overflow-x-auto">
                                <pre className="font-mono text-xs text-gray-300">
                                    {this.state.error.stack}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}
